import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Transition from '../../utils/Transition';

import { useAuth } from '../../hooks/AuthProvider';

const DropdownProfile = React.memo(({
  align
}) => {

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const auth = useAuth();

  const trigger = useRef(null);
  const dropdown = useRef(null);

  const handleDropdownToggle = useCallback(() => {
    setDropdownOpen(!dropdownOpen);
  }, [dropdownOpen]);

  const handleLogout = useCallback(() => {
    setDropdownOpen(!dropdownOpen);
    auth.logout();
  }, [dropdownOpen, auth]);

  const handleSettingsClick = useCallback(() => {
    setDropdownOpen(!dropdownOpen);
  }, [dropdownOpen]);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (!dropdownOpen || dropdown.current.contains(target) || trigger.current.contains(target)) return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [dropdownOpen]);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [dropdownOpen]);

  const dropdownClasses = useMemo(() => 
    `origin-top-right z-10 absolute top-full min-w-44 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 py-1.5 rounded shadow-lg overflow-hidden mt-1 ${align === 'right' ? 'right-0' : 'left-0'}`,
    [align]
  );

  return (
    <div className="relative inline-flex">
      <button
        ref={trigger}
        className="inline-flex justify-center items-center group"
        aria-haspopup="true"
        onClick={handleDropdownToggle}
        aria-expanded={dropdownOpen}
      >
        <img className="w-8 h-8 rounded-full" src={auth.user.user.avatar} width="32" height="32" alt="User" />
        <div className="flex items-center truncate">
          <span className="truncate ml-2 text-sm font-medium dark:text-slate-300 group-hover:text-slate-800 dark:group-hover:text-slate-200">{auth.user.user.fullname}</span>
          <svg className="w-3 h-3 shrink-0 ml-1 fill-current text-slate-400" viewBox="0 0 12 12">
            <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
          </svg>
        </div>
      </button>

      <Transition
        className={dropdownClasses}
        show={dropdownOpen}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
      >
        <div
          ref={dropdown}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setDropdownOpen(false)}
        >
         
          <ul>
            <li>
              <Link
                className="font-medium text-sm text-royalblue-500 hover:text-royalblue-600 dark:hover:text-royalblue-400 flex items-center py-1 px-3"
                to="/settings?page=myaccount"
                onClick={handleSettingsClick}
              >
                Paramètres
              </Link>
            </li>
            <li>
              <Link
                className="font-medium text-sm text-red-500 hover:text-red-600 dark:hover:text-red-400 flex items-center py-1 px-3"
                to="/login"
                onClick={handleLogout}
              >
                Se déconnecter
              </Link>
            </li>
          </ul>
        </div>
      </Transition>
    </div>
  )
});

DropdownProfile.displayName = 'DropdownProfile';

export default DropdownProfile;